import React from 'react';
import { Phone, Mail, MapPin, Facebook, Linkedin, Instagram, Lock, ExternalLink } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    'Loan Programs': [
      { name: 'Cash Out Refinance', href: '/loan-programs/cash-out-refinance' },
      { name: 'Hard Money', href: '/loan-programs/hard-money' },
      { name: 'Fix and Flip', href: '/loan-programs/fix-and-flip' },
      { name: 'New Construction', href: '/loan-programs/new-construction' },
      { name: 'Commercial / Multi-Family', href: '/loan-programs/commercial-multi-family' },
      { name: 'Short Term Rental', href: '/loan-programs/short-term-rental' }
    ],
    'Company': [
      { name: 'About Us', href: '/about' },
      { name: 'Our Team', href: '/team' },
      { name: 'Join Our Team', href: '/join' },
      { name: 'Reviews', href: '/#reviews' },
      { name: 'Contact', href: '/contact' }
    ],
    'Resources': [
      { name: 'DSCR Calculator', href: '/dscr-calculator' },
      { name: 'Fix & Flip Deal Calculator', href: '/deal-calculator' },
      { name: 'Learning Center', href: '/blog' },
      { name: 'FAQ', href: '/faq' }
    ]
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_130b504f-d356-4ffc-9f88-b9ceecab1da1/artifacts/djtji90d_logo_large%20-%20Edited.png" 
              alt="Close Now Lending - Real Estate Investment Lender in Houston, TX" 
              className="h-16 w-auto mb-4 brightness-0 invert scale-[2] origin-left ml-4"
            />
            <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
              Your trusted partner in real estate investment lending. With over 15 years of experience and 100s of projects funded, we're here to help you succeed.
            </p>
            <div className="space-y-3">
              <a href="tel:+19034762937" className="flex items-center text-gray-400 hover:text-white transition-colors group">
                <Phone className="h-5 w-5 mr-3 text-blue-500" />
                <span>903-476-2937</span>
              </a>
              <a href="mailto:info@closenowlending.com" className="flex items-center text-gray-400 hover:text-white transition-colors group">
                <Mail className="h-5 w-5 mr-3 text-blue-500" />
                <span>info@closenowlending.com</span>
              </a>
              <div className="flex items-start text-gray-400">
                <MapPin className="h-5 w-5 mr-3 text-blue-500 flex-shrink-0 mt-1" />
                <span>4601 Yale St<br />Houston, TX 77018</span>
              </div>
            </div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="text-white font-bold text-lg mb-4">{category}</h3>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.name}>
                    <a 
                      href={link.href} 
                      className="text-gray-400 hover:text-white transition-colors inline-block hover:translate-x-1 transform duration-200"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} Close Now Lending. All rights reserved.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center space-x-4">
              <a href="https://www.facebook.com/profile.php?id=61578576757231" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.linkedin.com/company/10860410" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/closenowlending/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
              
              {/* Admin Link */}
              <div className="h-8 w-px bg-gray-700 mx-2"></div>
              <a 
                href="/admin/login" 
                className="flex items-center space-x-2 text-gray-400 hover:text-blue-500 transition-colors text-sm"
                title="Admin Portal"
              >
                <Lock className="h-4 w-4" />
                <span className="hidden sm:inline">Admin</span>
              </a>
              <div className="h-8 w-px bg-gray-700 mx-2"></div>
              <a 
                href="https://app.lendingautomator.com/auth/signin" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-gray-400 hover:text-blue-500 transition-colors text-sm"
                title="Loan Officer CRM"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="hidden sm:inline">Loan Officer CRM</span>
              </a>
            </div>
            
            <div className="flex space-x-6 text-sm">
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;